import React, {FC} from "react";
import SleJourney from "../../../assets/images/sle-journey.png";
import SleIntegrationDiagram from "../../../assets/images/sle-integration-diagram.png";
import {Table, THead, TBody, TD, TH, TR} from '@carfax-stencils/table'
import CodeSnippetTemplate from "../../CodeSnippetTemplate/CodeSnippetTemplate";
import CodeSnippet from "../../../model/CodeSnippet";
import {CodeSnippetLanguages} from "../../CodeSnippetTemplate/CodeSnippetLanguages";
import {Link} from "react-router-dom";
import Constants from "../../Constants";

const ServiceLaneExperience: FC = () => {
    return (
        <section>
            <br/>
            <h1 id={"service-lane-experience"}>Service Lane Experience</h1>
            <p>
                <strong>*Please note that Service Lane Experience is not assigned by default to a Consumer Partner. This feature is available only to service shops and dealers enrolled in the CARFAX For Life (C4L) Program. Your
                    CARFAX Account Manager will work with you to determine if this is a suitable product for
                    your specific use case.</strong>
            </p>
            <h3 id="sle-overview">Overview</h3>
            <>
                <p>
                    Car Care is a mobile and web application offered by CARFAX which helps vehicle owners maintain their
                    cars more effectively through personalized maintenance reminders, recall alerts, and service history
                    tracking. Studies have consistently shown that Car Care users service more frequently, get recalls
                    fixed more
                    diligently, and make smarter ownership decisions. As a result, dealerships and aftermarket shops
                    prefer having their customers on Car Care, as they provide higher lifetime value by being “model”
                    service customers.
                </p>
                <p>
                    The CARFAX Service Lane Experience (SLE) is a personalized CARFAX link that enables car owners to
                    easily create their Car Care account to access all the features described above. By distributing
                    these SLE Links to their customers, dealers and service shops can more effectively integrate them into the Car
                    Care program. This has important benefits for both the shop and their CRM Partner (you).
                </p>
                <ol>
                    <li>
                        By becoming Car Care users, customers provide better lifetime value to their service shops through
                        more service visits and increased service loyalty. CARFAX also markets that shop to these
                        customers through regular emails and notifications, encouraging them to service and eventually
                        trade-in their vehicle at that location, if applicable.
                    </li>
                    <li>
                        The CRM Partner can provide a seamless “Sign-up for CARFAX Car Care” functionality to their
                        dealer and service shop customers out-of-the-box, a highly coveted feature for most service shops.
                    </li>
                </ol>
                <p>
                    The sections below explain how you; the CRM Partner, can build this feature into your dealer-facing
                    products.
                </p>
            </>
            <hr/>
            <h3 id="sle-user-journey">User Journey</h3>
            <>
                <p>
                    The typical user journey for CARFAX SLE is illustrated below.
                    <div className={"img-journey"}>
                        <img
                            src={SleJourney}
                            alt="Carfax Service Lane Experience Journey"
                            style={{marginBottom: 0}}
                        />
                        <p style={{marginTop: '0.25rem'}}>
                            <i>
                                <strong>Note:</strong> The screen which opens from the text message sent by dealer
                                changes
                                as per context of the message.
                            </i>
                        </p>
                    </div>
                </p>
                <ol>
                    <li>
                        The dealership/service shop decides to reach out to their customer with a CARFAX feature, such
                        as a
                        Service Review Solicitation, Free CARFAX Report, or Recall Check.
                    </li>
                    <li>
                        The Partner invokes the CARFAX Connect API with details about that customer and the shop.
                    </li>
                    <li>
                        CARFAX generates a personalized SLE Link and sends it as response via Connect to the Partner.
                    </li>
                    <li>
                        The Partner embeds this SLE Link in the message that the service shop sends to the customer
                        through
                        the Partner application.
                    </li>
                    <li>
                        The customer receives the shop's message containing the SLE Link. By clicking on it, they are
                        seamlessly enrolled in CARFAX Car Care and can start accessing details about their vehicle.
                    </li>
                    <li>
                        Additionally, CARFAX starts promoting the service shop to this customer through co-branded email
                        alerts and app notifications.
                    </li>
                </ol>
            </>
            <hr/>
            <h3 id="sle-integration-diagram">Integration Architecture Diagram</h3>
            <>
                <br/>
                The diagram below shows the flow of information between the four stakeholders of this process, namely –
                CARFAX, CRM Partner (you), Service Shop, and Car Owner.
                <div className={"img-integration-diagram"}>
                    <img
                        src={SleIntegrationDiagram}
                        alt="Carfax Service Lane Experience Integration Diagram"
                        style={{marginBottom: 0}}
                    />

                </div>
                <hr/>
            </>
            <h3 id="sle-map-service-shops">Map Service Shops</h3>
            <>
                <br/>
                <p>
                    Whenever a new Partner is set up, the first step is to sync service shop databases. This enables
                    CARFAX to identify which service shop is communicating with the customer.
                </p>
                <p>
                    To achieve this, CARFAX requires you to share following details about each of your dealer or
                    service
                    shop customers.
                </p>
                <Table aria-label="My Table" i18n="en">
                    <THead>
                        <TH width={50}><strong>#</strong></TH>
                        <TH width={150}><strong>Parameter</strong></TH>
                        <TH><strong>Description</strong></TH>
                    </THead>
                    <TBody>
                        <TR>
                            <td>1</td>
                            <td>Location ID</td>
                            <td>How you uniquely identify your dealer, 50-character limit, alphanumeric (special characters can include only hyphens, underscores, and periods)</td>
                        </TR>
                        <TR>
                            <td>2</td>
                            <td>Service Shop</td>
                            <td>100-character limit</td>
                        </TR>
                        <TR>
                            <td>3</td>
                            <td>Address</td>
                            <td>Physical address, not mailing. Example – 123 Elm Street, 60-character limit</td>
                        </TR>
                        <TR>
                            <td>4</td>
                            <td>City</td>
                            <td>60-character limit</td>
                        </TR>
                        <TR>
                            <td>5</td>
                            <td>State</td>
                            <td>Abbreviated and in CAPS, i.e. VA, NY etc.</td>
                        </TR>
                        <TR>
                            <td>6</td>
                            <td>Zip Code</td>
                            <td>5 characters with no spaces</td>
                        </TR>
                        <TR>
                            <td>7</td>
                            <td>Phone Number</td>
                            <td>10 characters with no punctuation, example – 7039342664</td>
                        </TR>
                        <TR>
                            <td>8</td>
                            <td>Shop/Dealer Website</td>
                            <td>Starting with www, <i>example - www.westpointvaford.com</i></td>
                        </TR>
                    </TBody>
                </Table>
                <br/>
                <p>
                    The file you share must be <strong>Pipe delimited (|)</strong> text, columns encapsulated with
                    double quotes. E.g.
                </p>
                <code>
                    "LocationID"|"DealerName"|"Address1"|"Address2"|"City"|"State"|"ZipCode"|"PhoneNumber"|"Website"|
                </code>
                <p>
                    If we are unable to map any of your locations using the above list, we may request additional
                    information such as Fax #, Toll Free #, Primary Contact Info, to assist in the matching process.
                </p>
                <p>
                    We recommend sending this file <strong>daily</strong> and ensuring it reflects your full
                    refreshed list of service shops so that CARFAX can get new shops up-and-running more quickly.
                </p>
                <p>
                    <strong>File Name</strong> – <i>[partnerName]_dealerlist_mmddyyyy.csv</i>
                </p>
                <p>
                    <strong>FTP Details</strong>
                </p>
                <ul>
                    <li><strong>URL</strong> - ftp://data.carfax.com</li>
                    <li><strong>ID & Password</strong> – To be Provided over Zoom call</li>
                    <li>Upload the file during non-peak hours (12 am - 6am CST).</li>
                </ul>
                <p>
                    Please note that the dealer file should be a <strong>full replacement file</strong> and not
                    adds/deletes. We will assume that any  dealer/shop that has dropped out of the file since it was last
                    processed is no longer valid.
                </p>
                <br/>
            </>
            <h3 id="sle-connect-setup">SLE Link Generation</h3>
            <>
                <p>
                    The next step is to build the capability of invoking the CARFAX Connect SLE Link API.
                </p>
                <h4>Prerequisites</h4>
                <>
                    <p>
                        Before you can authenticate and start running queries on CARFAX Connect, you must be configured
                        by CARFAX for the Service Lane Experience and have a <strong>Client Credentials
                        Application</strong> set up in the Partner Portal. You can choose to use the one we provided
                        (App Type - Client Credentials) or one that you create.
                    </p>
                    <p>
                        To get started, a developer on your team will receive an invitation to the CARFAX Connect
                        Partner Portal as noted on the Authentication page. Please share an email address where we
                        can send this invite. From the invite email, you'll be prompted to set up a password. After you
                        log in to the Portal, you can follow steps outlined in the above link to finish setting up your
                        account.
                    </p>
                    <p>
                        Once you have access to the Partner Portal, create a Client Credentials application – Client
                        ID and Client Secret. Next, authenticate to receive an access token (JWT). This token allows
                        you to make GraphQL queries to CARFAX Connect to receive Car Care Check information for a
                        given vehicle.
                    </p>
                    <p>
                        Additionally, you will need to provide two items to your Account Manager so that we can
                        register
                        you on the Partner Portal.
                    </p>
                    <ol>
                        <li>
                            Indicate whether you want CARFAX to shorten the URL before passing it back to you. This
                            choice is made at the Partner level; if you choose Yes, CARFAX will send back shortened
                            URLs for all your requests.
                        </li>
                        <li>
                            Provide a list of use cases for which your dealers want to message their customers with
                            the CARFAX SLE link. Currently supported campaigns include:
                            <ul>
                                <li>Free CARFAX Report</li>
                                <li>Service Review Solicitation</li>
                            </ul>
                        </li>
                    </ol>
                    <p>
                        <strong>IMPORTANT:</strong> An access token is valid for 24 hours and should be reused until
                        it expires. Please do not fetch a new access token with each request. If implemented
                        properly, we expect to see one refresh per day if calling from a single machine or up to 10
                        if using distributed machines. The expiration is identified via <code>expires_in</code>,
                        which is in seconds. Be sure to obtain a fresh token sufficiently far in advance to account
                        for network delays and clock skew.
                    </p>
                    <p>
                        Refer to this documentation page for more information on how to establish the Client
                        Credentials application and authenticate - <a
                        href="http://developer.carfax.com/docs/connect/authentication#client-credentials-flowhttp://developer.carfax.com/docs/connect/authentication#client-credentials-flow">Client
                        Credentials Authentication</a>
                    </p>

                    <h4>Sending a Request</h4>
                    <>
                        <p>
                            Once you have successfully authenticated and obtained an access token (JWT), you can
                            make a
                            request to CARFAX Connect to receive a CARFAX SLE Link whenever an eligible service shop
                            needs to communicate a CARFAX benefit to their customers through your platform.
                        </p>
                        <p>
                            Your request to the CARFAX Connect API will have the following parameters:
                        </p>
                        <Table aria-label="SLE API Parameters" i18n="en">
                            <THead>
                                <TH width={25}><strong>#</strong></TH>
                                <TH width={75}><strong>Parameter</strong></TH>
                                <TH width={100}><strong>Format</strong></TH>
                                <TH width={300}><strong>Description</strong></TH>
                            </THead>
                            <TBody>
                                <TR>
                                    <TD>1</TD>
                                    <TD>VIN<span className={"text-red"}>*</span></TD>
                                    <TD>17 characters (alpha-numeric)</TD>
                                    <TD>Identifies the car whose details are to be shown</TD>
                                </TR>
                                <TR>
                                    <TD>2</TD>
                                    <TD>Location ID<span className={"text-red"}>*</span></TD>
                                    <TD>Alpha-numeric</TD>
                                    <TD>Identifies which service shop needs to be marketed to the customer after they've
                                        interacted with the SLE Link</TD>
                                </TR>
                                <TR>
                                    <TD>3</TD>
                                    <TD>User Phone Number<span className={"text-red"}>*</span></TD>
                                    <TD>10 digits without any punctuation</TD>
                                    <TD>Allows us to uniquely identify the car owner so that we can market the
                                        service shop to that customer
                                    </TD>
                                </TR> <TR>
                                <TD>4</TD>
                                <TD>Email</TD>
                                <TD>Valid Email Format</TD>
                                <TD>Allows customer to access CARFAX Car Care without having to manually enter the
                                    email during sign-up</TD>
                            </TR>
                                <TR>
                                    <TD>5</TD>
                                    <TD>Service Date<span className={"text-red"}>*</span></TD>
                                    <TD>yyyy-mm-dd format</TD>
                                    <TD>Allows us to determine what items were repaired during the customer's
                                        service
                                        visit, by matching against the CARFAX Report of that VIN</TD>
                                </TR>
                                <TR>
                                    <TD>6</TD>
                                    <TD>Campaign Name<span className={"text-red"}>*</span></TD>
                                    <TD>Rate Service / Free VHR</TD>
                                    <TD>Allows us to determine appropriate user journey after customer clicks on the
                                        SLE
                                        Link</TD>
                                </TR>
                            </TBody>
                        </Table>
                        <i><span className={"text-red"}>*</span> Denotes a mandatory field</i>
                        <p>
                            The following is the GraphQL Schema that you may use to make the request to CARFAX
                            Connect:

                            <CodeSnippetTemplate codeSnippets={[
                                new CodeSnippet(CodeSnippetLanguages.GRAPHQL,
                                    "GRAPHQL",
                                    "query ServiceLaneExperience {\n" +
                                    "    serviceLaneExperience(\n" +
                                    "        vin: \"4T4BF3EK7BR105263\"\n" +
                                    "        campaignName: \"Rate Service\"\n" +
                                    "        email: \"TEST@CARFAX.COM\"\n" +
                                    "        locationId: \"11111\"\n" +
                                    "        userPhoneNumber: \"1234567890\"\n" +
                                    "        serviceDate: \"2025-05-15\"\n" +
                                    "    ) { \n" +
                                    "       campaignName \n" +
                                    "       locationId \n" +
                                    "       url \n" +
                                    "       vin \n" +
                                    "       expirationDate \n" +
                                    "   }\n" +
                                    "}\n"
                                )
                            ]}/>
                        </p>
                        <p>
                            <strong>Example Curl:</strong>
                            <CodeSnippetTemplate codeSnippets={[
                                new CodeSnippet(
                                    CodeSnippetLanguages.BASH,
                                    "BASH",
                                    "curl --request POST \\\n" +
                                    "  --url https://connect.carfax.com/v1/graphql \\\n" +
                                    "  --header 'Authorization: BEARER YOUR_ACCESS_TOKEN' \\\n" +
                                    "  --header 'Content-Type: application/json' \\\n" +
                                    "  --header 'User-Agent: insomnia/11.2.0' \\\n" +
                                    "  --data '{\n" +
                                    "    \"query\": \"query ServiceLaneExperience {\\n  serviceLaneExperience(\\n    vin: \\\"4T4BF3EK7BR105263\\\"\\n    campaignName: \\\"Rate Service\\\"\\n    email: \\\"test@CARFAX.COM\\\"\\n    locationId: \\\"11111\\\"\\n    userPhoneNumber: \\\"0123456789\\\"\\n    serviceDate: \\\"2025-01-03\\\"\\n  ) {\\n    campaignName\\n    locationId\\n    url\\n    vin\\n    expirationDate\\n  }\\n}\\n\",\n" +
                                    "    \"operationName\": \"ServiceLaneExperience\"\n" +
                                    "  }'"
                                )
                            ]}/>
                        </p>
                        <p>
                            <strong>Production API Endpoint</strong>
                        </p>
                        <p>
                            The request is sent via a Post request to
                            – <code>https://connect.carfax.com/v1/graphql</code>
                        </p>
                        {/*TODO this needs to be a link*/}
                        <p>
                            You may find full details on how to format and send CARFAX Connect queries on the
                            Sending
                            Queries page <a href="#">here</a>.
                        </p>
                    </>
                    <h4>Receiving a Response</h4>
                    <>
                        <p>
                            The CARFAX response will be the SLE Link in a JSON format with the following details:The
                            CARFAX
                            response will be the SLE Link in a JSON format with the following details:
                        </p>
                        <Table aria-label="SLE Response Parameters" i18n="en">
                            <THead>
                                <TH width={25}><strong>#</strong></TH>
                                <TH width={150}><strong>Parameter</strong></TH>
                                <TH><strong>Description</strong></TH>
                            </THead>
                            <TBody>
                                <TR>
                                    <TD>1</TD>
                                    <TD>VIN, Location ID, Campaign Name</TD>
                                    <TD>These three fields are the same as those in the request. They help you
                                        uniquely
                                        identify the request for which the response has been sent.</TD>
                                </TR>
                                <TR>
                                    <TD>2</TD>
                                    <TD>URL</TD>
                                    <TD>CARFAX SLE Link. If you have requested for a shortened URL, that will be
                                        returned</TD>
                                </TR>
                                <TR>
                                    <TD>3</TD>
                                    <TD>Expiration Date</TD>
                                    <TD>Date which denotes validity of the SLE Link, usually set to 30 days from
                                        link
                                        generation in yyyy-mm-dd format</TD>
                                </TR>
                                <TR>
                                    <TD>4</TD>
                                    <TD>Error Code</TD>
                                    <TD>Appropriate failure reason if a SLE Link cannot be returned.</TD>
                                </TR>
                            </TBody>
                        </Table>
                        <p>
                            The following is a sample JSON response that you will receive from CARFAX Connect:
                        </p>
                        <CodeSnippetTemplate codeSnippets={[
                            new CodeSnippet(CodeSnippetLanguages.JSON,
                                "JSON",
                                JSON.stringify({
                                        "data": {
                                            "serviceLaneExperience": {
                                                "vin": "4T4BF3EK7BR105263",
                                                "locationId": "11111",
                                                "campaignName": "Rate Service",
                                                "url": "https://sampleresponseurl.com/",
                                                "expirationDate": "2025-06-14"
                                            }
                                        }
                                    },
                                    null,
                                    2
                                )
                            )
                        ]}/>
                    </>
                    <br/>
                    <h4>Error Handling</h4>
                    <p>
                        Error handling for CARFAX SLE is unique as there can be multiple reasons why CARFAX is
                        unable to return a valid SLE Link. Refer to <Link to={`${Constants.ERROR_HANDLING_PAGE_URL}#error-codes`}>Error Handling</Link> for how these errors will be returned in the JSON response for
                        different scenarios.
                    </p>
                </>
                <hr/>
            </>
            <h3 id="sle-dealer-eligibility">Shop Eligibility Check</h3>
            <>
                <p>
                  This feature is available only to service shops and dealers enrolled in the CARFAX For Life (C4L) Program.
                  We recommend limiting this functionality on your end to only those locations to avoid continuous "Not Eligible" errors during link generation.
                </p>
                <p>
                    The same CARFAX Connect SLE Link API described above can be used to check service shop eligibility.
                    We recommend you run your list of locations against this API daily to receive the latest status of each shop.
                    It is especially important to check the status again at the start of each month as dealers and shops roll on/off the C4L Program at this time.
                </p>
                <p>
                    To achieve this, you can call the CARFAX Connect SLE Link API with the following parameters:
                    <ol>
                        <li>VIN</li>
                        <li>Location ID</li>
                        <li>Campaign Name (Rate Service OR Free VHR)</li>
                    </ol>
                </p>
                <p>
                    Since this API call is to check eligibility, please use the following invalid VIN
                    – <code>ABCDEFGH123456789</code>.
                </p>
                <p>
                    If Location ID is eligible, we will send an <code>INVALID_VIN</code> error. While if Location ID
                    is
                    ineligible, we will send a <code>SHOP_NOT_ON_C4L</code> error.
                </p>
                <p>
                    You can call this API for each of your Location IDs which are enrolled in this program with the
                    above parameters and switch off this feature for those IDs where you receive
                    a <code>SHOP_NOT_ON_C4L</code> error.
                </p>
                <p>
                    We recommend that you perform this check during non-peak hours (12 am - 6am CST).
                </p>
                <p>
                    <strong>Note</strong> – Please use a 17-character alpha-numeric value for the VIN, since only
                    that
                    will return the desired error message allowing you to determine eligibility.
                </p>
                <hr/>
            </>
            <h3 id="sle-rate-limits">Rate Limits on CARFAX Connect</h3>
            <>
                <p>
                   CARFAX Connect has rate and quota limits for all partners. For details, see the <Link to={Constants.RATE_QUOTA_LIMITS_URL}>Rate & Quota Limits</Link> page.
                </p>
                <hr/>
            </>
            <h3 id="sle-testing">Testing the Integration</h3>
            <>
                <p>
                    For testing this integration, we recommend you create two Test Location IDs with the following
                    details
                </p>
                <Table aria-label="Test Dealers" i18n="en">
                    <THead>
                        <TH width={200}><strong>Test Dealer Name</strong></TH>
                        <TH><strong>Test Dealer Address</strong></TH>
                    </THead>
                    <TBody>
                        <TR>
                            <TD>TESTSVCLANE</TD>
                            <TD>5860 Trinity Pkwy, Centreville, VA 20120, USA</TD>
                        </TR>
                        <TR>
                            <TD>TESTNOC4L</TD>
                            <TD>5860 Trinity Pkwy, Centreville, VA 20120, USA</TD>
                        </TR>
                    </TBody>
                </Table>
                <p>
                    Whenever you want to test if links are getting generated properly, please use these two location
                    IDs. We would map these to Test Dealers on our end to ensure that no live dealer or service shop accounts are
                    impacted while testing. The first Test Dealer is eligible for the SLE program, so you would
                    receive
                    a valid SLE link, while the second one is not eligible, so you would receive a SHOP_NOT_ON_C4L
                    error.
                </p>
                <p>
                    After configuring this CARFAX SLE Link, you can create corresponding workflows within your
                    application and allow eligible service shops (as determined by CARFAX) to send this link to
                    their
                    customers at appropriate touchpoints, inviting them to avail the CARFAX Car Care benefits
                    described
                    earlier.
                </p>
                <p>
                    We look forward to working with you in our mission to provide a superior car ownership
                    experience to
                    customers of our dealer and service shop partners.
                </p>
                <hr/>
            </>
        </section>
    );
};

export default ServiceLaneExperience;
